<script setup lang="ts">
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmHost from "./components/ConfirmHost.vue";
import ToastHost from "./components/ToastHost.vue";
import AppShell, { type AppShellNavGroup, type AppShellNavItem } from "./layouts/AppShell.vue";
import LoginView from "./views/LoginView.vue";
import { api, setUnauthorizedHandler, type Item } from "./api";
import { useAuth } from "./composables/useAuth";
import { allowsAny, routePermissions } from "./access";
import { homePathFor } from "./home";
import { useRealtime } from "./composables/useRealtime";

const router = useRouter();
const route = useRoute();
const auth = useAuth();
const brand = reactive({ name: "任务管理系统", mark: "R", image: "" });
const counts = reactive<Record<string, number>>({ tasks: 0, approvals: 0, office: 0 });
const activeKey = computed(() => String(route.name ?? "dashboard"));

const groups = computed<AppShellNavGroup[]>(() => [
  // 「风险事件」已并入任务中心（不再单独新建，历史事件在任务里点关联事件查看），菜单里不再单列
  { label: "工作台", items: [
    { key: "dashboard", label: "工作台", icon: "layout-dashboard" },
    // 任务中心徽标 = 待我处理（我参与且未结束的任务）
    { key: "tasks", label: "任务中心", icon: "list-checks", badge: counts.tasks || undefined }
  ] },
  { label: "审批与审计", items: [
    { key: "approvals", label: "审批", icon: "badge-check", badge: counts.approvals || undefined },
    { key: "audit", label: "操作日志", icon: "scroll-text" }
  ] },
  { label: "协作", items: [
    { key: "duty", label: "值班管理", icon: "calendar" },
    { key: "notifications", label: "通知", icon: "megaphone" },
    // 文档已并入文件管理（文档统一以 .xlsx 呈现），不再单列「文档中心」入口
    { key: "files", label: "文件管理", icon: "folder" },
    { key: "risk", label: "风控中心", icon: "shield" },
    { key: "office", label: "综合办公", icon: "briefcase", badge: counts.office || undefined }
  ] },
  { label: "管理", items: [
    // 统计报表暂时下线（重新设计中）：入口先不挂，页面代码保留
    { key: "settings", label: "系统配置", icon: "settings" }
  ] }
].map(group => ({
  ...group,
  items: group.items.filter(item => allowsAny(auth.state.user, routePermissions[item.key]))
})).filter(group => group.items.length));

async function loadBrand() {
  try {
    const settings = await api.get<any>("/catalog");
    const basic = settings.basic ?? {};
    brand.name = basic.systemName || "任务管理系统";
    document.title = brand.name;
    brand.image = basic.logo || "";
    if (basic.icon) {
      let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = basic.icon;
    }
  } catch {}
}
function enterWorkbench() {
  // 登录后进角色配置的默认首页（角色管理 → 默认首页，默认工作台）
  const target = homePathFor(auth.state.user);
  if (route.path !== target) void router.replace(target);
  void loadBrand();
}
async function loadCounts() {
  try {
    const [tasks, approvals, office] = await Promise.all([
      api.tryGet<Item[]>("/tasks", []),
      api.tryGet<Item[]>("/approvals", []),
      api.tryGet<{ pending: number }>("/office/pending-count", { pending: 0 })
    ]);
    // 任务中心徽标 = 待我处理：我参与（或在成员里、或我是负责人）且还没结束的任务
    const myKeys = [String(auth.state.user?.id ?? ""), String(auth.state.user?.name ?? "")].filter(Boolean);
    const mine = (item: Item) => {
      // 创建者不在 members 里，单独算进来；负责人（接受任务后写入）也算
      const people = [
        String(item.creatorId ?? ""), String(item.creator ?? ""),
        ...(Array.isArray(item.members) ? item.members.map(String) : []),
        String(item.ownerId ?? ""), String(item.owner ?? "")
      ].filter(Boolean);
      return people.some(key => myKeys.includes(key));
    };
    counts.tasks = tasks.filter(item => String(item.status ?? "") === "进行中" && mine(item)).length;
    counts.approvals = approvals.filter(item => item.status === "待审批").length;
    counts.office = Number(office?.pending) || 0;
  } catch {}
}
async function onSignedIn() {
  enterWorkbench();
}
async function logout() {
  await auth.logout();
  await router.replace("/login");
}
function navigate(item: AppShellNavItem) {
  router.push({ name: item.key });
}
function userAction(action: "profile" | "settings") {
  router.push({ name: action === "profile" ? "me" : "account" });
}
function toggleTheme() {
  document.documentElement.classList.toggle("dark");
  localStorage.setItem("riskops_theme", document.documentElement.classList.contains("dark") ? "dark" : "light");
}
function broadcastChange(type: string) {
  window.dispatchEvent(new CustomEvent("riskops:change", { detail: { type } }));
}
useRealtime(event => {
  let type = "";
  try { type = String(JSON.parse(event.data)?.type ?? ""); } catch {}
  broadcastChange(type);
}, auth.signedIn, () => {
  // 实时连接断线重连成功：中间可能漏了变更事件，让所有视图补拉一次。
  broadcastChange("*");
});
// 会话过期（401）时清登录态并跳登录页，避免用户看到一堆「请求失败：401」。
setUnauthorizedHandler(() => {
  auth.clearSession();
  if (route.name !== "login") void router.replace("/login");
});
let countsTimer = 0;
window.addEventListener("riskops:change", () => {
  window.clearTimeout(countsTimer);
  countsTimer = window.setTimeout(() => void loadCounts(), 300);
});
watch(() => auth.signedIn.value, (signed) => {
  if (signed && route.name === "login") enterWorkbench();
});
onMounted(async () => {
  if (localStorage.getItem("riskops_theme") === "dark") document.documentElement.classList.add("dark");
  await auth.restore();
  if (auth.signedIn.value) {
    await loadBrand();
    await loadCounts();
    if (route.name === "login" || route.name === "home") await router.replace(homePathFor(auth.state.user));
  } else if (route.name !== "login") {
    await router.replace("/login");
  }
});

const shellUser = computed(() => {
  const user = auth.state.user as Item | null;
  return {
    name: String(user?.name ?? "Jason"),
    meta: `${String(user?.department || "未分配部门")} · ${String(user?.role || "成员")}`,
    initials: String(user?.name ?? "J").slice(0, 1)
  };
});
</script>

<template>
  <div v-if="!auth.state.ready" class="ro-boot">正在加载 RiskOps…</div>
  <LoginView v-else-if="!auth.signedIn.value" @signed-in="onSignedIn" />
  <AppShell
    v-else
    :groups="groups"
    :active-key="activeKey"
    :brand-name="brand.name"
    :brand-mark="brand.mark"
    :brand-image="brand.image"
    :user="shellUser"
    @navigate="navigate"
    @user-action="userAction"
    @toggle-theme="toggleTheme"
    @logout="logout"
  >
    <RouterView />
  </AppShell>
  <ConfirmHost />
  <ToastHost />
</template>

<style scoped>
.ro-boot{min-height:100vh;display:grid;place-items:center;background:var(--ro-bg);color:var(--ro-text)}
</style>
